import { Link } from 'react-router-dom';
import { CREDIT_COPY, DEFAULT_DISCLAIMER, UNPUBLISHED_RANGE, formatRange, formatUsd, type usePublicPricing } from '@/lib/pricing';

type Pricing = ReturnType<typeof usePublicPricing>;

interface Props {
  pricing: Pricing;
  slug: string;
  showFee?: boolean;
  showLink?: boolean;
  className?: string;
}

// Compact indicative price line. Never shows $0 or a fabricated number when nothing is published.
export default function PriceNote({ pricing, slug, showFee = true, showLink = false, className = '' }: Props) {
  const { state, data, priceFor, reload } = pricing;
  if (state === 'loading') return <p className={`text-xs text-foreground-600 ${className}`}>Loading typical repair cost…</p>;
  if (state === 'error') {
    return (
      <p className={`text-xs text-foreground-600 ${className}`}>
        Typical pricing couldn&apos;t be loaded.{' '}
        <button type="button" onClick={reload} className="cursor-pointer whitespace-nowrap underline">Retry</button>
      </p>
    );
  }
  const p = priceFor(slug);
  const fee = data?.diagnostic_fee;
  return (
    <div className={`border-l-2 border-primary-200 pl-4 ${className}`}>
      <p className="text-xs uppercase tracking-[0.12em] text-foreground-600">Typical repair cost</p>
      <p className="mt-1 text-base text-foreground-950">{p ? formatRange(p) : UNPUBLISHED_RANGE}</p>
      <p className="mt-1 text-xs leading-relaxed text-foreground-600">{p ? (data?.disclaimer || DEFAULT_DISCLAIMER) : 'Your technician will provide a quote after diagnosing the issue.'}</p>
      {showFee && fee && (
        <p className="mt-2 text-xs leading-relaxed text-foreground-700">
          {fee.amount_cents > 0 ? `Diagnostic visit fee: ${formatUsd(fee.amount_cents)}.` : 'No diagnostic visit fee.'}
          {fee.credited_toward_repair ? ` ${CREDIT_COPY}` : ''}
        </p>
      )}
      {showLink && (
        <Link to="/pricing" className="mt-2 inline-flex whitespace-nowrap text-xs text-primary-700 underline-offset-2 hover:underline">How pricing works</Link>
      )}
    </div>
  );
}